/**
 * TV1-2291 (§6 · t25) — WHY a readmodel-backed tile looks the way it does.
 *
 * The sibling of `kpiState.ts`, for the four types whose source is a PV3 readmodel
 * rather than Cube: `count`, `alert`, `list`, `progress`. Same shape of answer —
 * a discriminated union a renderer CANNOT read past without handling each member —
 * and DOM-free for the same reason: *why* a tile is empty is a semantic question,
 * so it is decided and tested without a renderer.
 *
 * ## The four causes, and why they are NOT the KPI's four
 *
 * `kpiState` distinguishes *prerequisite-missing* / *empty-period* /
 * *no-prior-window* / *error*, because a Cube measure can be `NULL` for reasons
 * that have nothing to do with the window. A readmodel query has none of those
 * problems: it either ran or it did not, and if it ran it returned some number of
 * rows. So the four causes here are:
 *
 * ```
 * no payload                       -> loading      (absence is NOT emptiness)
 * fetch failed                     -> error        (an unknown collection, a 401, a missing wrapper key)
 * payload is not the readmodel shape -> unavailable  (see below — never a confident 0)
 * ran, nothing matched             -> empty        (a REAL ANSWER, and often good news)
 * ran, rows came back              -> ready
 * ```
 *
 * ## ⚠️ `empty` is MEASURED ZERO, and that is the opposite call from `kpiData`
 *
 * `kpiData.sumMeasure` returns `null` — never `0` — when Cube gives it nothing,
 * because a `NULL` measure means *"we did not measure"*. A readmodel count is not
 * like that: the query ran, the predicate was evaluated against every row, and
 * **nothing matched**. That is a measurement, and its value is zero. Rendering
 * "0 quiebres de stock" is the correct, useful and frequently *reassuring* answer;
 * rendering an em-dash for it would be the mirror-image lie — hiding a real
 * measurement behind the glyph that means "unknown".
 *
 * The tiles still SAY so (`ReadmodelEmptyHint`), because a bare `0` cannot tell a
 * reader whether nothing matched or the widget is broken. That is the whole point
 * of separating `empty` from `unavailable`.
 *
 * ## Why `unavailable` exists at all
 *
 * `fetchReadmodelWidgetData` always returns `{ rows, count }`, so in production the
 * shape is guaranteed. It is not guaranteed at the RENDERER, which is also reached
 * by a persisted layout whose `type` was changed from `barChart` to `count` (the
 * card keeps its Cube payload for one render), by a test fixture, and by the AI
 * preview path. Falling through to `count: 0` there would print a confident zero
 * derived from a payload that was never a count — the single failure mode this
 * whole ticket's readmodel catalog is built to prevent. So: a named state, and the
 * tiles render it as "no se pudo leer", not as a number.
 */

import { isReadmodelWidgetData, type ReadmodelWidgetData } from '../widgetData';
import type { ReadmodelRow } from '../readmodelSource';

/** Shown when the fetch failed without saying how. Never blank. */
const UNSPECIFIED_ERROR = 'The query failed';

export type ReadmodelWidgetState =
  /** No payload yet. Waiting, NOT empty — a tile must not claim "nothing matched" mid-fetch. */
  | { kind: 'loading' }
  /** The query failed. Distinct from every empty. */
  | { kind: 'error'; message: string }
  /** A payload arrived that is not `{ rows, count }`. Never rendered as a number. */
  | { kind: 'unavailable' }
  /** The query ran and nothing matched. A MEASURED zero — see the module header. */
  | { kind: 'empty' }
  /** Rows came back. */
  | { kind: 'ready'; rows: ReadmodelRow[]; count: number };

/**
 * Read the fetch-error envelope off an `unknown`.
 *
 * Structural rather than `isWidgetDataError`, for the reason `kpiState.readFetchError`
 * gives: that guard needs its argument pre-narrowed to `WidgetDataState`, which at
 * this seam means casting an `unknown` nobody has checked. Keep the two in step.
 */
const readFetchError = ( data: unknown ): string | null => {
  if ( typeof data !== 'object' || data === null ) return null;

  const envelope = data as { status?: unknown; error?: unknown };
  if ( envelope.status !== 'error' ) return null;

  return typeof envelope.error === 'string' && envelope.error ? envelope.error : UNSPECIFIED_ERROR;
};

/** Unwrap `{ status: 'ok', data }`; pass anything else through untouched. */
const unwrapPayload = ( data: unknown ): unknown => {
  if ( typeof data !== 'object' || data === null ) return data;

  const envelope = data as { status?: unknown; data?: unknown };
  return envelope.status === 'ok' ? envelope.data : data;
};

/**
 * Decide why a readmodel-backed tile looks the way it does.
 *
 * Accepts EITHER the `WidgetDataState` envelope or the already-unwrapped payload,
 * exactly as `resolveKpiState` does: `WebWidget` unwraps before it reaches a parser
 * (and handles `loading`/`error` itself), while a test — and any future caller that
 * skips the chokepoint — hands the envelope straight in. Handling both here is what
 * lets every tile be correct on its own terms rather than on its caller's.
 */
export const resolveReadmodelWidgetState = ( data: unknown ): ReadmodelWidgetState => {
  // Absence is not emptiness.
  if ( data === undefined || data === null ) return { kind: 'loading' };

  const fetchError = readFetchError( data );
  if ( fetchError ) return { kind: 'error', message: fetchError };

  const payload = unwrapPayload( data );
  if ( !isReadmodelWidgetData( payload )) return { kind: 'unavailable' };

  const { rows, count } = payload as ReadmodelWidgetData;

  // Both zero: the query ran and matched nothing. A `count` of 0 with rows present
  // is impossible from `fetchReadmodelWidgetData` (the base-route branch derives the
  // count FROM the rows), and a positive `count` with no rows is a `limit: 0` query
  // — a total that is real even though nothing was listed, so it is `ready` and the
  // list renders its own "nothing to show" line.
  if ( count === 0 && rows.length === 0 ) return { kind: 'empty' };

  return { kind: 'ready', rows, count };
};

/**
 * The count a tile should DISPLAY, for the two number tiles.
 *
 * `0` for `empty` — the measured zero — and `null` for every state where a number
 * would be fabricated. Returning `0` for `unavailable` or `error` is the confident-
 * zero failure this module exists to prevent, so it is not reachable from here.
 */
export const readmodelDisplayCount = ( state: ReadmodelWidgetState ): number | null => {
  if ( state.kind === 'empty' ) return 0;
  if ( state.kind === 'ready' ) return state.count;
  return null;
};
